import { useMemo, useState } from 'react'
import {
  type LearnerAccount,
  type AccountsState,
  isLearnerAccount,
  getLinkedParents,
} from '../../../accounts'

interface UseLinkChildOptions {
  accounts: AccountsState
  parentId: string
  linkedLearners: LearnerAccount[]
  linkParentToLearnerById: (parentId: string, learnerId: string) => void
}

interface UseLinkChildReturn {
  linkCode: string
  setLinkCode: (code: string) => void
  error: string
  success: string
  pendingLearner: LearnerAccount | null
  pendingLearnerParents: { id: string; displayName: string }[]
  handleSubmit: (e: React.FormEvent) => void
  handleConfirmLink: () => void
  handleCancelLink: () => void
}

export function useLinkChild({
  accounts,
  parentId,
  linkedLearners,
  linkParentToLearnerById,
}: UseLinkChildOptions): UseLinkChildReturn {
  const [linkCode, setLinkCode] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pendingLearner, setPendingLearner] = useState<LearnerAccount | null>(
    null,
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setPendingLearner(null)

    const code = linkCode.trim().toUpperCase()
    if (!code) {
      setError('Vul een koppelcode in.')
      return
    }

    const allAccounts = Object.values(accounts.accountsById)
    const learner = allAccounts.find(
      (a) => isLearnerAccount(a) && a.linkCode === code,
    ) as LearnerAccount | undefined

    if (!learner) {
      setError(
        'Geen leerling gevonden met deze koppelcode. Controleer de code en probeer opnieuw.',
      )
      return
    }

    if (linkedLearners.some((l) => l.id === learner.id)) {
      setError('Deze leerling is al gekoppeld aan jouw account.')
      return
    }

    setPendingLearner(learner)
  }

  const handleConfirmLink = () => {
    if (!pendingLearner) return
    linkParentToLearnerById(parentId, pendingLearner.id)
    setSuccess(`${pendingLearner.displayName} is succesvol gekoppeld!`)
    setLinkCode('')
    setPendingLearner(null)
  }

  const handleCancelLink = () => {
    setPendingLearner(null)
  }

  const pendingLearnerParents = useMemo(() => {
    if (!pendingLearner) return []
    return getLinkedParents(accounts, pendingLearner.id).filter(
      (p) => p.id !== parentId,
    )
  }, [pendingLearner, accounts, parentId])

  return {
    linkCode,
    setLinkCode,
    error,
    success,
    pendingLearner,
    pendingLearnerParents,
    handleSubmit,
    handleConfirmLink,
    handleCancelLink,
  }
}
